/**
 * WHICH QUESTIONS A SCREEN ASKS, decided once per archetype instead of once per view.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THIS FILE EXISTS
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * The rules layer has scaled by plant type for a long time: `templates.ts` keys a parameter
 * template off an exhaustive `Record<ArchetypeId, SiteTemplate>`, so adding an archetype is a
 * COMPILE ERROR until its template exists, and `standards/scope.ts` decides applicability by
 * named group with the reasoning attached rather than by a hand-kept list.
 *
 * The view layer had no equivalent. When a renewable microgrid was onboarded into a console
 * built for a telecom central office, the Digital Twin's KPI rail was a fixed DC/telecom set,
 * so the solar plant rendered:
 *
 *     DC LOAD  —        DC BUS  — (Rectifier output)
 *     BATTERY SOC  — (0 strings)    UPS LOAD  — (worst phase)
 *
 * Four em-dashes, which read as four broken instruments rather than four inapplicable
 * questions. The fix at the time was one `archetype === 'renewable'` ternary inside the view —
 * correct in behaviour and wrong in shape, because the next view copies it and the two drift.
 * This file is that ternary paid back.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * FOUR RULES, AND EACH ONE IS A DECISION THAT WAS GOT WRONG FIRST
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * 1. KEYED ON ARCHETYPE, NEVER ON INDUSTRY. `applicable()` filters standards by archetype
 *    alone and the engine never reads industry at all (ADR-0006). Industry is a single
 *    nullable enum used as a VISIBILITY hint elsewhere, and using it to decide anything is
 *    how a real inverter became invisible to a real microgrid — migration 0035: "The role
 *    existed the whole time; the filter hid it." `archetype-not-industry.test.ts` fails the
 *    build if a view reaches for the other axis.
 *
 * 2. RESOLVED PER SITE, NEVER ONCE PER PAGE. `organisations` carries no archetype column;
 *    both taxonomy columns live on `sites`, and one organisation holds `centraloffice` AND
 *    `renewable` today. So a fleet list renders mixed plant types, and a card set chosen once
 *    for a page would be wrong for half its rows. Call `plantView(site.archetype)` inside the
 *    row, exactly as the engine calls `siteTemplate(site.archetype)`.
 *
 * 3. EXHAUSTIVE, so a new archetype cannot be half-added. Same enforcement as `TEMPLATES`.
 *    Adding one to `ARCHETYPE_IDS` is a type error here until somebody decides what its
 *    screens should ask — which is the work that adding an archetype is SUPPOSED to cost.
 *
 * 4. A CARD THAT CANNOT BE ANSWERED IS ABSENT, not em-dashed. This registry says which
 *    questions a plant type can answer at all. It does NOT say whether today's telemetry
 *    happens to answer them — a card whose data is missing on a plant that should have it is
 *    a real gap and must still show as one.
 *
 * PURE. Constants and lookups, no React, no I/O — so it can be tested as data and consumed
 * by any view, chart or export without dragging a component tree along.
 */
import { ARCHETYPE_IDS, type ArchetypeId } from '@/lib/compliance/types';

/**
 * Named archetype groups, so a spec below states a REASON rather than a list.
 *
 * Deliberately parallel to `standards/scope.ts`, which groups the same ids for the same kind
 * of decision. Where a group here matches one there, the two must not disagree — a plant
 * graded as generation whose screens ask about rectifiers is the contradiction this whole
 * file exists to prevent.
 */

/**
 * Plants whose job is to MAKE power. `scope.ts` groups these two for its DER and STORAGE
 * scopes for the same reason: both convert and export rather than merely consume, so the
 * first question about either is what it is producing, not what its rectifier bus reads.
 *
 * Storage is in because a battery plant's questions are output, state of charge and cycle
 * count — closer to a solar farm's than to a data hall's, even though the medium differs.
 */
const GENERATION: readonly ArchetypeId[] = ['renewable', 'storage'];

/**
 * ICT facilities — plants built to keep computing or radio equipment alive. The original five
 * archetypes, and the shape every screen in this console was written against.
 *
 * These are the sites with a rectifier, a DC bus, a battery string and a UPS, which is why
 * the DC card set is theirs and why it produced four em-dashes anywhere else.
 */
const ICT: readonly ArchetypeId[] = ['datacenter', 'cellsite', 'centraloffice', 'edge5g', 'fmps'];

/**
 * Every card a plant view can show, as an id rather than a component.
 *
 * IDS, NOT COMPONENTS, on purpose. This module stays pure and testable, and the same
 * declaration can drive a KPI rail, a printed report or a CSV column set without three
 * places agreeing on which cards a plant type has.
 */
export type PlantCard =
  /* Universal — every plant has a power chain, so every plant can be graded on it. */
  | 'redundancy'
  | 'openAlarms'
  /*
   * ICT plant, read off a bank's metered panels (NBR Kigali, 2026-10-16): what the site runs
   * on, what it draws, its UPS feeders' share, the standby set and the 110 V supply. Shown only
   * where the meters report them (the rail drops an unanswerable card), so a telecom site with
   * a rectifier plant and no panel meters keeps the set below and nothing else.
   */
  | 'supplyNow'
  | 'siteLoad'
  | 'upsFeeders'
  | 'generatorNow'
  | 'dcSupply'
  /* ICT plant: the DC/telecom set. */
  | 'dcLoad'
  | 'dcBus'
  | 'batterySoc'
  | 'upsLoad'
  /* Generation plant. */
  | 'pvOutput'
  | 'gridImport'
  | 'yieldToday'
  | 'irradiance'
  | 'plantState'
  | 'solarShare'
  | 'moduleTemp'
  | 'assertedFaults';

export interface PlantViewSpec {
  /**
   * The KPI cards this plant type can answer, IN READING ORDER.
   *
   * Order is part of the declaration, not a rendering detail: the first card is what an
   * operator should see first, and on every plant type that is the compliance verdict.
   */
  readonly kpis: readonly PlantCard[];
  /** One line, in the operator's terms, for why this set and not another. */
  readonly why: string;
}

/**
 * The ICT set, unchanged from what every telecom and data-centre screen has always shown.
 *
 * `openAlarms` last is deliberate — it is the only card here that is not about the plant's
 * electrical state, so it closes the row rather than interrupting it.
 */
const ICT_VIEW: PlantViewSpec = {
  kpis: [
    'redundancy',
    'supplyNow',
    'siteLoad',
    'upsFeeders',
    'generatorNow',
    'dcSupply',
    'dcLoad',
    'dcBus',
    'batterySoc',
    'upsLoad',
    'openAlarms',
  ],
  why: 'A plant built to keep equipment alive: a rectifier bus, a battery string and a UPS are the things that decide whether it stays up.',
};

/**
 * The generation set.
 *
 * `plantState` earns its place because without it `pvOutput: 0.0 kW` is correct at two in the
 * morning and identical on screen to a dead plant. `solarShare` is the microgrid's own
 * question — how much of the load the array is actually carrying. `assertedFaults` replaces
 * `openAlarms` on plants whose payload carries no alarm slots at all, where the machines
 * report their own condition through a state register instead.
 */
const GENERATION_VIEW: PlantViewSpec = {
  kpis: [
    'redundancy',
    'pvOutput',
    'gridImport',
    'yieldToday',
    'irradiance',
    'plantState',
    'solarShare',
    'moduleTemp',
    'assertedFaults',
  ],
  why: 'A plant whose job is to produce: what it is making, what the site is drawing, and whether the array is lit or at rest.',
};

/**
 * Plants that consume but are not ICT facilities — a hospital, a factory, a refinery, a
 * traction substation, an office, an EV hub.
 *
 * THEY GET THE UNIVERSAL SET AND NOTHING ELSE, WHICH IS THE HONEST ANSWER TODAY. Each of
 * these has real questions of its own — a hospital's isolated-power panels, a refinery's
 * hazardous-area classification, a charging hub's per-bay utilisation — and none of them has
 * been designed. Handing them the DC/telecom set because it is the one that exists would put
 * a rectifier bus on a hospital and four em-dashes on a factory, which is exactly the failure
 * this registry was built to stop.
 *
 * So the rail is short and everything on it is true. When one of these plant types gets real
 * cards, it gets its own entry here rather than borrowing somebody else's.
 */
const CONSUMER_VIEW: PlantViewSpec = {
  kpis: ['redundancy', 'openAlarms'],
  why: 'No card set designed for this plant type yet — showing only what is true of every plant, rather than borrowing another type’s questions.',
};

/**
 * EXHAUSTIVE BY CONSTRUCTION. Adding an id to `ARCHETYPE_IDS` breaks this line until someone
 * decides what that plant's screens should ask.
 */
export const PLANT_VIEWS: Record<ArchetypeId, PlantViewSpec> = {
  /* ICT — the five the console was written for. */
  datacenter: ICT_VIEW,
  cellsite: ICT_VIEW,
  centraloffice: ICT_VIEW,
  edge5g: ICT_VIEW,
  fmps: ICT_VIEW,
  /* Generation. */
  renewable: GENERATION_VIEW,
  storage: GENERATION_VIEW,
  /* Consumers with no designed set — see `CONSUMER_VIEW`. */
  healthcare: CONSUMER_VIEW,
  industrial: CONSUMER_VIEW,
  hazardous: CONSUMER_VIEW,
  transport: CONSUMER_VIEW,
  commercial: CONSUMER_VIEW,
  evcharging: CONSUMER_VIEW,
};

/**
 * The card set for one site. Mirrors `siteTemplate(archetype)` deliberately — same call
 * shape, same axis, so the screens and the engine cannot disagree about what a plant is.
 *
 * An unrecognised value falls back to the universal set rather than throwing: the archetype
 * arrives as a string from the API, and a console that renders nothing because the server
 * learned a new plant type is worse than one that shows the two cards that are always true.
 */
export function plantView(archetype: string | null | undefined): PlantViewSpec {
  if (!archetype) return CONSUMER_VIEW;
  return PLANT_VIEWS[archetype as ArchetypeId] ?? CONSUMER_VIEW;
}

/** True when this plant type's screens should ask generation questions. */
export function isGenerationArchetype(archetype: string | null | undefined): boolean {
  return archetype !== null && archetype !== undefined
    && (GENERATION as readonly string[]).includes(archetype);
}

/** Whether one card belongs on this site's rail. */
export function showsCard(archetype: string | null | undefined, card: PlantCard): boolean {
  return plantView(archetype).kpis.includes(card);
}

/** Exported for the tests, and so a reader can see the groups are real rather than implied. */
export const ARCHETYPE_GROUPS = { GENERATION, ICT, ALL: ARCHETYPE_IDS } as const;
